import {
  Box,
  Button,
  Heading,
  Input,
  Stack,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Checkbox,
  IconButton,
  Textarea,
  Select,
  useToast,
  useColorModeValue,
  Radio,
  RadioGroup,
  Radio as CRadio,
  Text,
  FormErrorMessage,
  Image,
  Textarea as ChakraTextarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { Field, FieldArray, Form, Formik } from "formik";
import { useState, useMemo, useRef, useEffect } from "react";
import * as Yup from "yup";
import { GoTrashcan } from "react-icons/go";
import { BiCheckbox, BiCheckboxChecked } from "react-icons/bi";
import { useMutation, useQuery } from "react-query";
import { addManualQuestion } from "API/questionApi";
import { getSubjects } from "API/subjectApi";
import { useAppSelector } from "app/hook";
import useErrorToast from "hook/error-toast";
import TagInput from "components/TagInput";
import { SubmitButton } from "formik-chakra-ui";


const ALLOWED_TYPES = ["image/jpeg", "image/png"];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB


const isValidFile = (file: File | null) =>
  !file || (ALLOWED_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE);


function getFileError(file: File | null) {
  if (!file) return null;
  if (!ALLOWED_TYPES.includes(file.type)) return "Only JPG or PNG files allowed.";
  if (file.size > MAX_FILE_SIZE) return "File must be 2 MB or less.";
  return null;
}

// Custom OptionInput component that supports both text and image paste
interface OptionInputProps {
  value: string;
  onChange: (value: string) => void;
  onImageChange: (file: File | null) => void;
  imageFile: File | null;
  imageError: string | null;
  placeholder?: string;
}

const OptionInput: React.FC<OptionInputProps> = ({
  value,
  onChange,
  onImageChange,
  imageFile,
  imageError,
  placeholder = "Enter option text or paste image"
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (!editorRef.current) return;
    if (!imageFile) {
      // Keep the contentEditable text in sync with value when no image attached
      if (editorRef.current.innerText !== value) {
        editorRef.current.innerText = value || "";
      }
    } else {
      // If image attached, show empty text area (still paste-enabled)
      if (editorRef.current.innerText !== "") {
        editorRef.current.innerText = "";
      }
    }
  }, [value, imageFile]);
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const dt = e.clipboardData;
    if (!dt) return;

    // Prefer items API when available
    const items = dt.items;
    if (items && items.length) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const error = getFileError(file);
            if (error) return;
            onImageChange(file);
            return;
          }
        }
      }
    }

    // Fallback to files list (some browsers expose only files)
    const files = dt.files;
    if (files && files.length) {
      const file = files[0];
      if (file && file.type && file.type.startsWith('image/')) {
        e.preventDefault();
        const error = getFileError(file as any);
        if (error) return;
        onImageChange(file as any);
        return;
      }
    }
    // Otherwise, allow default text paste
  };

  const hasImage = Boolean(imageFile);
  const previewUrl: string | null = useMemo(() => {
    if (!hasImage || !imageFile) return null;
    return URL.createObjectURL(imageFile as File);
  }, [hasImage, imageFile]);

  const imagePreview = useMemo<JSX.Element | null>(() => {
    if (!hasImage) return null;
    return (
      <HStack mt={2} spacing={3} align="center">
        {previewUrl && (
          <Image
            src={previewUrl}
            alt="Option preview"
            maxH="60px"
            maxW="80px"
            objectFit="contain"
            borderWidth="1px"
            borderRadius="md"
            onClick={onOpen}
            cursor="pointer"
          />
        )}
        <Button size="sm" onClick={onOpen}>Preview</Button>
        <IconButton
          aria-label="Remove image"
          icon={<GoTrashcan />}
          size="sm"
          colorScheme="red"
          onClick={() => onImageChange(null)}
        />
        <Text fontSize="sm" color="gray.600">Image attached</Text>
      </HStack>
    );
  }, [hasImage, previewUrl, onOpen, onImageChange]);

  return (
    <Box>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e: React.FormEvent<HTMLDivElement>) => {
          if (hasImage) return; // ignore text changes when an image is attached
          const text = (e.currentTarget as HTMLDivElement).innerText;
          onChange(text);
        }}
        onPaste={handlePaste}
        p={2}
        borderWidth="1px"
        borderRadius="md"
        minH="40px"
        whiteSpace="pre-wrap"
        outline="none"
        _focusWithin={{ boxShadow: "outline" }}
        aria-label={placeholder}
      />
      {imagePreview}
      {imageError ? (
        <Text color="red.400" fontSize="sm" mt={1}>
          {imageError}
        </Text>
      ) : null}

      {/* Full preview modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={4}>
            {hasImage ? (
              <Image
                src={previewUrl as string}
                alt="Option full preview"
                maxH="70vh"
                objectFit="contain"
                mx="auto"
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
      {!hasImage && (!value || value.trim() === '') ? (
        <Text mt={1} fontSize="xs" color="gray.500">Type here or press Ctrl+V to paste an image</Text>
      ) : null}
    </Box>
  );
};


type QuestionFormValues = {
  questionType: string;
  subject: string;
  level: string;
  questionText: string;
  options: string[];
  correctAnswers: string[];
  correctAnswer: string;
  explanation: string;
  tags: string[];
  positiveMarks: number;
  negativeMarks: number;
  questionImg: string;
  justificationImg: string;
};


// Correct answer required only for TF
const validationSchema = Yup.object().shape({
  questionType: Yup.string().required("Question type is required"),
  subject: Yup.string().required("Subject is required"),
  level: Yup.string().required("Level is required"),
  questionText: Yup.string().required("Question is required"),
  options: Yup.array().when("questionType", {
    is: (val: string) => val === "MCQS" || val === "MCQM",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  correctAnswers: Yup.array().when("questionType", {
    is: (val: string) => val === "MCQS" || val === "MCQM",
    then: (schema) => schema.min(1, "Select at least one answer"),
    otherwise: (schema) => schema.notRequired(),
  }),
  correctAnswer: Yup.string().when("questionType", {
    is: (val: string) => val === "TF",
    then: (schema) => schema.required("Please select True or False"),
    otherwise: (schema) => schema.notRequired(),
  }),
  positiveMarks: Yup.number().typeError("Enter a valid number").required("Required"),
  negativeMarks: Yup.number().typeError("Enter a valid number").required("Required"),
  questionImg: Yup.string().notRequired(),
  justificationImg: Yup.string().notRequired(),
});


const ManualInsertQuestion = () => {
  const toast = useToast();
  const { role } = useAppSelector((state) => state.auth);


  const { data: subjects, error: subjectError, isLoading: subjectsLoading } =
    useQuery("subjects", getSubjects);


  useErrorToast(subjectError);


  const { mutateAsync, isLoading } = useMutation(addManualQuestion, {
    onSuccess: () => {
      toast({
        title: "Question inserted",
        description: "Manual question was successfully added.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (err: any) => {
      let msg = err?.message || "Something went wrong.";
      if (
        err?.code === 11000 ||
        (typeof err?.message === "string" && /duplicate/i.test(err.message))
      ) {
        msg = "Duplicate question or already exists. Please use a unique question.";
      }
      toast({
        title: "Error",
        description: msg,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });


  const [tags, setTags] = useState<string[]>([]);
  const [mcqsSingle, setMcqsSingle] = useState<number | null>(null);
  const [questionImgFile, setQuestionImgFile] = useState<File | null>(null);
  const [justificationImgFile, setJustificationImgFile] = useState<File | null>(null);
  const [optionImages, setOptionImages] = useState<(File | null)[]>([]);

  const [questionImgError, setQuestionImgError] = useState<string | null>(null);
  const [justificationImgError, setJustificationImgError] = useState<string | null>(null);
  const [optionImageErrors, setOptionImageErrors] = useState<(string | null)[]>([]);


  const initialValues = useMemo<QuestionFormValues>(() => {
    const defaultSub = subjects?.[0]?.name || "";
    return {
      questionType: "MCQS",
      subject: defaultSub,
      level: "Normal",
      questionText: "",
      options: ["", "", ""],
      correctAnswers: [],
      correctAnswer: "",
      explanation: "",
      tags: [],
      questionImg: "",
      justificationImg: "",
      positiveMarks: 2,
      negativeMarks: -1,
    };
  }, [subjects]);

  // Initialize option images array when options change
  useMemo(() => {
    if (optionImages.length !== initialValues.options.length) {
      setOptionImages(new Array(initialValues.options.length).fill(null));
      setOptionImageErrors(new Array(initialValues.options.length).fill(null));
    }
  }, [initialValues.options.length]);


  if (subjectsLoading) return <Box>Loading subjects...</Box>;


  return (
    <Box maxW="800px" mx="auto" p={5} boxShadow="md" borderRadius="md" bg={useColorModeValue("white", "gray.700")}>
      <Heading mb={5}>Insert Manual Question</Heading>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          console.log("Form submission started", { values, optionImages });
          console.log("correctAnswers before submission:", values.correctAnswers);

          // Validation temporarily removed for testing
          console.log("Proceeding without validation", { 
            type: values.questionType,
            answers: values.correctAnswers,
            options: values.options,
            images: optionImages 
          });
          
          // Validate all files
          if (!isValidFile(questionImgFile)) {
            console.log("Question image validation failed");
            setQuestionImgError(getFileError(questionImgFile));
            return;
          }
          if (!isValidFile(justificationImgFile)) {
            console.log("Justification image validation failed");
            setJustificationImgError(getFileError(justificationImgFile));
            return;
          }
          
          // Validate option images
          let hasOptionImageError = false;
          const newOptionImageErrors = optionImages.map((file, index) => {
            const error = getFileError(file);
            if (error) hasOptionImageError = true;
            return error;
          });
          
          if (hasOptionImageError) {
            console.log("Option image validation failed", newOptionImageErrors);
            setOptionImageErrors(newOptionImageErrors);
            return;
          }
          
          setQuestionImgError(null);
          setJustificationImgError(null);
          setOptionImageErrors(new Array(optionImages.length).fill(null));

          try {
            const formData = new FormData();
            formData.append("questionType", values.questionType);
            formData.append("subject", values.subject);
            formData.append("level", values.level);
            formData.append("questionText", values.questionText);
            formData.append("explanation", values.explanation);
            formData.append("positiveMarks", String(values.positiveMarks));
            formData.append("negativeMarks", String(values.negativeMarks));
            tags.forEach((tag) => formData.append("tags[]", tag));

            if (["MCQS", "MCQM"].includes(values.questionType)) {
              // Send option text and image information
              values.options.forEach((opt, index) => {
                const hasImage = optionImages[index] !== null && optionImages[index] !== undefined;
                const optionText = hasImage && !opt.trim() ? `[IMAGE_OPTION_${index}]` : opt;
                formData.append("options[]", optionText);
              });

              // Process correct answers
              const processedAnswers = values.correctAnswers.map(ans => {
                const index = parseInt(ans);
                if (isNaN(index)) return ans;
                // For image-only options, use the placeholder text as the answer
                const hasImage = optionImages[index] !== null && optionImages[index] !== undefined;
                const hasText = values.options[index]?.trim();
                return hasImage && !hasText ? `[IMAGE_OPTION_${index}]` : ans;
              });
              
              processedAnswers.forEach(ans => formData.append("correctAnswers[]", ans));
              
              // Add option images
              optionImages.forEach((file, index) => {
                if (file) {
                  // ensure a consistent filename extension if clipboard provides blob without name
                  const namedFile = new File([file], file.name || `option_${index}.png`, { type: file.type || 'image/png' });
                  formData.append(`optionImageFile_${index}`, namedFile);
                }
              });
            } else if (values.questionType === "TF") {
              formData.append("correctAnswer", values.correctAnswer);
            } else {
              if (values.correctAnswer?.trim()) {
                formData.append("correctAnswer", values.correctAnswer.trim());
              }
            }

            if (questionImgFile) {
              formData.append("questionImageFile", questionImgFile);
            }
            if (justificationImgFile) {
              formData.append("justificationImageFile", justificationImgFile);
            }

            console.log("Submitting to API...");
            await mutateAsync(formData);
            console.log("Submission successful!");
            
            actions.resetForm();
            setTags([]);
            setMcqsSingle(null);
            setQuestionImgFile(null);
            setJustificationImgFile(null);
            setOptionImages(new Array(initialValues.options.length).fill(null));
          } catch (error) {
            console.error("Submission failed:", error);
            // handled by onError above
          }
        }}
      >
        {(formikProps) => {
          const { values, setFieldValue, errors, touched } = formikProps;
          
          return (
            <>
            <Form>
            <Stack direction="row" spacing={8}>
              {/* Left pane */}
              <VStack w="30%" spacing={6} align="stretch">
                <FormControl isRequired isInvalid={!!(touched.subject && errors.subject)}>
                  <FormLabel>Subject</FormLabel>
                  <Select
                    value={values.subject}
                    onChange={(e) => setFieldValue("subject", e.target.value)}
                  >
                    {subjects?.map((sub: any) => {
                      const name = sub.name || '';
                      return (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      );
                    })}
                  </Select>
                  <FormErrorMessage>{errors.subject as any}</FormErrorMessage>
                </FormControl>


                <FormControl isRequired isInvalid={!!(touched.questionType && errors.questionType)}>
                  <FormLabel>Question Type</FormLabel>
                  <Select
                    value={values.questionType}
                    onChange={(e) => {
                      setFieldValue("questionType", e.target.value);

                      if (e.target.value === "MCQS" || e.target.value === "MCQM") {
                        setMcqsSingle(null);
                        setFieldValue("options", ["", "", ""]);
                        setFieldValue("correctAnswers", []);
                        setFieldValue("correctAnswer", "");
                        setOptionImages([null, null, null]);
                        setOptionImageErrors([null, null, null]);
                      } else {
                        setMcqsSingle(null);
                        setFieldValue("options", []);
                        setFieldValue("correctAnswers", []);
                        setFieldValue("correctAnswer", "");
                        setOptionImages([]);
                        setOptionImageErrors([]);
                      }
                    }}
                  >
                    <option value="MCQS">MCQ (Single Choice)</option>
                    <option value="MCQM">MCQ (Multiple Choice)</option>
                    <option value="TF">True / False</option>
                    <option value="Short">Short Answer</option>
                    <option value="LA">Long Answer</option>
                    <option value="diagram">Diagram</option>
                  </Select>
                  <FormErrorMessage>{errors.questionType as any}</FormErrorMessage>
                </FormControl>


                <FormControl isRequired isInvalid={!!(touched.level && errors.level)}>
                  <FormLabel>Level</FormLabel>
                  <Select
                    value={values.level}
                    onChange={(e) => setFieldValue("level", e.target.value)}
                  >
                    <option value="Normal">Normal</option>
                    <option value="Easy">Easy</option>
                    <option value="Difficult">Difficult</option>
                  </Select>
                  <FormErrorMessage>{errors.level as any}</FormErrorMessage>
                </FormControl>


                <FormControl>
                  <FormLabel>Tags</FormLabel>
                  <TagInput tags={tags} setTags={setTags} />
                </FormControl>
              </VStack>


              {/* Right pane */}
              <Box flex="1">
                <VStack align="stretch" spacing={5}>
                  {/* Marks section - removed questionMarks field */}
                  <HStack spacing={4}>
                    <FormControl isRequired isInvalid={!!(touched.positiveMarks && errors.positiveMarks)}>
                      <FormLabel>Positive Marks</FormLabel>
                      <Field name="positiveMarks">
                        {({ field }: any) => (
                          <Input
                            {...field}
                            type="number"
                            value={values.positiveMarks}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              setFieldValue("positiveMarks", isNaN(val) ? 0 : val);
                            }}
                          />
                        )}
                      </Field>
                      <FormErrorMessage>{errors.positiveMarks as any}</FormErrorMessage>
                    </FormControl>


                    <FormControl isRequired isInvalid={!!(touched.negativeMarks && errors.negativeMarks)}>
                      <FormLabel>Negative Marks</FormLabel>
                      <Field name="negativeMarks">
                        {({ field }: any) => (
                          <Input
                            {...field}
                            type="number"
                            value={values.negativeMarks}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              setFieldValue("negativeMarks", isNaN(val) ? 0 : val);
                            }}
                          />
                        )}
                      </Field>
                      <FormErrorMessage>{errors.negativeMarks as any}</FormErrorMessage>
                    </FormControl>
                  </HStack>


                  <FormControl isRequired isInvalid={!!(touched.questionText && errors.questionText)}>
                    <FormLabel>Question</FormLabel>
                    <Field name="questionText" as={Input} />
                    <FormErrorMessage>{errors.questionText as any}</FormErrorMessage>
                  </FormControl>


                  {(values.questionType === "MCQS" || values.questionType === "MCQM") && (
                    <FieldArray name="options">
                      {(arrayHelpers) => (
                        <Box>
                          <FormLabel>Options</FormLabel>
                          {values.options.map((option: string, idx: number) => (
                            <VStack key={idx} mb={3} align="stretch">
                              <HStack align="flex-start" spacing={2}>
                                <Box flex="1">
                                  <OptionInput
                                    value={option}
                                    onChange={(value: string) => setFieldValue(`options.${idx}`, value)}
                                    onImageChange={(file: File | null) => {
                                      const newOptionImages = [...optionImages];
                                      newOptionImages[idx] = file;
                                      setOptionImages(newOptionImages);
                                      
                                      // Update option text based on whether image is present
                                      if (file) {
                                        setFieldValue(`options.${idx}`, `[IMAGE_OPTION_${idx}]`);
                                      } else if (option.startsWith('[IMAGE_')) {
                                        setFieldValue(`options.${idx}`, '');
                                      }
                                    }}
                                    imageFile={optionImages[idx] || null}
                                    imageError={optionImageErrors[idx] || null}
                                    placeholder={`Option ${idx + 1} - Enter text or paste image`}
                                  />
                                </Box>
                                <VStack spacing={1}>
                                  {values.questionType === "MCQS" ? (
                                    <Button
                                      size="sm"
                                      colorScheme={mcqsSingle === idx ? "teal" : "gray"}
                                      onClick={() => {
                                        const optionText = values.options[idx];
                                        const hasImage = optionImages[idx] !== null && optionImages[idx] !== undefined;
                                        if (!optionText && !hasImage) {
                                          toast({
                                            title: "Validation Error",
                                            description: "Selected answer must have either text or an image.",
                                            status: "error",
                                            duration: 4000,
                                            isClosable: true,
                                          });
                                          return;
                                        }
                                        setMcqsSingle(idx);
                                        setFieldValue("correctAnswers", [optionText]);
                                      }}
                                      title="Select as correct"
                                    >
                                      {mcqsSingle === idx ? (
                                        <BiCheckboxChecked size="20px" />
                                      ) : (
                                        <BiCheckbox size="20px" />
                                      )}
                                    </Button>
                                  ) : (
                                    <Checkbox
                                      isChecked={values.correctAnswers.includes(option)}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        const current = values.correctAnswers || [];
                                        if (checked) {
                                          setFieldValue("correctAnswers", [...current, option]);
                                        } else {
                                          setFieldValue(
                                            "correctAnswers",
                                            current.filter((val: string) => val !== option)
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                  {
                                    <IconButton
                                      aria-label="Remove option"
                                      icon={<GoTrashcan />}
                                      size="sm"
                                      colorScheme="red"
                                      onClick={() => {
                                        arrayHelpers.remove(idx);
                                        // Remove corresponding image
                                        const newOptionImages = [...optionImages];
                                        newOptionImages.splice(idx, 1);
                                        setOptionImages(newOptionImages);
                                        const newOptionImageErrors = [...optionImageErrors];
                                        newOptionImageErrors.splice(idx, 1);
                                        setOptionImageErrors(newOptionImageErrors);
                                      }}
                                    />
                                  }
                                </VStack>
                              </HStack>
                            </VStack>
                          ))}
                          {touched.options && typeof errors.options === "string" && (
                            <Text color="red.400" fontSize="sm">{errors.options}</Text>
                          )}
                          {values.options.length < 6 && (
                            <Button
                              size="sm"
                              onClick={() => {
                                arrayHelpers.push("");
                                // Add null to option images array
                                setOptionImages([...optionImages, null]);
                                setOptionImageErrors([...optionImageErrors, null]);
                              }}
                              mt={2}
                              colorScheme="teal"
                            >
                              Add Option
                            </Button>
                          )}
                        </Box>
                      )}
                    </FieldArray>
                  )}


                  {values.questionType === "TF" && (
                    <FormControl
                      isRequired
                      isInvalid={!!(touched.correctAnswer && errors.correctAnswer)}
                    >
                      <FormLabel>Correct Answer</FormLabel>
                      <RadioGroup
                        onChange={(val) => setFieldValue("correctAnswer", val)}
                        value={values.correctAnswer}
                      >
                        <HStack spacing={6}>
                          <CRadio value="True">True</CRadio>
                          <CRadio value="False">False</CRadio>
                        </HStack>
                      </RadioGroup>
                      <FormErrorMessage>{errors.correctAnswer as any}</FormErrorMessage>
                    </FormControl>
                  )}


                  {["Short", "LA", "diagram"].includes(values.questionType) && values.questionType !== "TF" && (
                    <FormControl isInvalid={!!(touched.correctAnswer && errors.correctAnswer)}>
                      <FormLabel>Correct Answer (optional)</FormLabel>
                      <Field name="correctAnswer" as={Input} />
                      <FormErrorMessage>{errors.correctAnswer as any}</FormErrorMessage>
                    </FormControl>
                  )}


                  {/* Upload Question Image with preview */}
                  <FormControl>
                    <FormLabel>Question Image</FormLabel>
                    <Input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => {
                        const f = e.currentTarget.files?.[0] || null;
                        const err = getFileError(f);
                        setQuestionImgError(err);
                        setQuestionImgFile(!err ? f : null);
                        if (err) {
                          toast({
                            title: "Invalid File",
                            description: err,
                            status: "warning",
                          });
                        }
                      }}
                    />
                    <Text fontSize="sm" color="gray.500">
                      Only JPG or PNG, max 2 MB.
                    </Text>
                    {questionImgError && (
                      <Text color="red.400" fontSize="sm">{questionImgError}</Text>
                    )}
                    {questionImgFile && (
                      <Box mt={2}>
                        <Image
                          src={URL.createObjectURL(questionImgFile)}
                          alt="Question preview"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </FormControl>


                  {/* Upload Justification Image with preview */}
                  <FormControl>
                    <FormLabel>Justification Image</FormLabel>
                    <Input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => {
                        const f = e.currentTarget.files?.[0] || null;
                        const err = getFileError(f);
                        setJustificationImgError(err);
                        setJustificationImgFile(!err ? f : null);
                        if (err) {
                          toast({
                            title: "Invalid File",
                            description: err,
                            status: "warning",
                          });
                        }
                      }}
                    />
                    <Text fontSize="sm" color="gray.500">
                      Only JPG or PNG, max 2 MB.
                    </Text>
                    {justificationImgError && (
                      <Text color="red.400" fontSize="sm">{justificationImgError}</Text>
                    )}
                    {justificationImgFile && (
                      <Box mt={2}>
                        <Image
                          src={URL.createObjectURL(justificationImgFile)}
                          alt="Justification preview"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </FormControl>


                  <FormControl>
                    <FormLabel>Answer Explanation</FormLabel>
                    <Field name="explanation" as={Textarea} />
                  </FormControl>


                  <HStack spacing={4} mt={6}>
                    <SubmitButton
                      colorScheme="teal"
                      isLoading={isLoading}
                      width="auto"
                    >
                      Save
                    </SubmitButton>
                    <Button variant="outline" width="auto" type="reset">
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Stack>
          </Form>
          </>
          ) as JSX.Element;
        }}
      </Formik>
    </Box>
  );
};


export default ManualInsertQuestion;
